/**
 * Email Template Service
 * Handles email template management API calls (Admin only)
 */
import BaseApiService from '../base/BaseApiService';
import type { EmailTemplate, EmailTemplateDetail, EmailTemplatePreviewResponse } from '@app-types';

class EmailTemplateService extends BaseApiService {
  /**
   * Get all email templates
   */
  async getEmailTemplates(): Promise<EmailTemplate[]> {
    const response = await this.get<{ success: boolean; data: EmailTemplate[] }>('/admin/email-templates');
    return response.data;
  }

  /**
   * Get a single email template by code
   */
  async getEmailTemplate(code: string): Promise<EmailTemplateDetail> {
    const response = await this.get<{ success: boolean; data: EmailTemplateDetail }>(`/admin/email-templates/${code}`);
    return response.data;
  }

  /**
   * Update an email template.
   * `subject` is sent when the admin has edited the subject line; the backend
   * validates that required {{...}} placeholders survive the edit (400 otherwise).
   */
  async updateEmailTemplate(
    code: string,
    textBlocks: Record<string, string>,
    subject?: string
  ): Promise<EmailTemplateDetail> {
    const response = await this.put<{ success: boolean; data: EmailTemplateDetail }>(`/admin/email-templates/${code}`, {
      text_blocks: textBlocks,
      ...(subject !== undefined && { subject })
    });
    return response.data;
  }

  /**
   * Preview an email template with given text blocks and (optionally) subject.
   * Returns the rendered HTML plus the resolved subject line.
   */
  async previewEmailTemplate(
    code: string,
    textBlocks: Record<string, string>,
    subject?: string
  ): Promise<EmailTemplatePreviewResponse> {
    const response = await this.post<{ success: boolean; html: string; subject?: string }>(
      `/admin/email-templates/${code}/preview`,
      {
        text_blocks: textBlocks,
        ...(subject !== undefined && { subject })
      }
    );
    return { html: response.html, subject: response.subject };
  }

  /**
   * Reset an email template to default
   */
  async resetEmailTemplate(code: string): Promise<EmailTemplateDetail> {
    const response = await this.post<{ success: boolean; data: EmailTemplateDetail }>(
      `/admin/email-templates/${code}/reset`
    );
    return response.data;
  }
}

export const emailTemplateService = new EmailTemplateService();
export default emailTemplateService;
